import { Platform } from '@angular/cdk/platform';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    effect,
    ElementRef,
    HostListener,
    inject,
    input,
    signal,
    viewChild,
    ViewEncapsulation,
} from '@angular/core';
import gsap from '@RalbaStudio/gsap';

import { NgClass } from '@angular/common';
import { AuiIconComponent } from '@ral/ui:aui/icons';
import { injectWindowsDimensions } from '@ral/ui:gui';
import { Draggable } from '@RalbaStudio/gsap/Draggable';
import { InertiaPlugin } from '@RalbaStudio/gsap/InertiaPlugin';
import ScrollTrigger from '@RalbaStudio/gsap/ScrollTrigger';
import { StoryImageComponent, StoryImageSourceComponent } from '@storyteller/image';
import { AuiLoadingComponent } from '@ral/ui:aui/aui-loading';
type SliderItem = {
    url: string;
    isPortrait: boolean;
    cumulativeWith: number;
    width: number;
    alt: string;
};
@Component({
    selector: 'app-carousel',
    imports: [StoryImageComponent, NgClass, StoryImageSourceComponent, AuiIconComponent, AuiLoadingComponent],
    template: `
        <div aria-live="polite" aria-atomic="true">
            @defer (on immediate) {
                <div #wrapper class="relative h-[160px] max-w-full md:h-[316px]">
                    <div #boxes class="opacity-1 absolute flex transition-opacity duration-200">
                        @for (item of items(); track $index) {
                            <div class="absolute overflow-hidden">
                                <div class="px-[15px]">
                                    <div
                                        class="relative h-[160px] w-auto overflow-hidden rounded md:h-[316px]"
                                        (click)="openImage($index)"
                                    >
                                        <story-image
                                            fetchPriority="low"
                                            class="block h-full rounded"
                                            [ngClass]="{
                                                'aspect-[1825/2738]': item.isPortrait,
                                                'aspect-[2738/1825]': !item.isPortrait,
                                            }"
                                            [loading]="'lazy'"
                                            [alt]="item.alt + ' preview'"
                                        >
                                            <story-source
                                                [src]="item.url"
                                                [width]="item.isPortrait ? 210 : 474"
                                                [height]="316"
                                            />
                                        </story-image>
                                    </div>
                                </div>
                            </div>
                        }
                    </div>
                </div>

                <div #proxy></div>

                @let openGalleryData = openGalleryDataSet();
                @if (openGalleryData !== null) {
                    <div
                        class="fixed inset-0 z-50 pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] text-surface-background"
                        role="dialog"
                        aria-modal="true"
                    >
                        <div class="absolute bottom-0 left-0 right-0 top-0 bg-info/85" (click)="closeImage()"></div>
                        <div class="absolute flex h-full w-full items-center justify-center">
                            <div
                                class="absolute h-[calc(100%-80px)] min-h-0 w-[calc(100%-80px)] min-w-0 transition-opacity duration-200"
                                [ngClass]="{
                                    'aspect-[21000/31600]': openGalleryData.index.isPortrait,
                                    'aspect-[47400/31600]': !openGalleryData.index.isPortrait,
                                    'opacity-0': loading(),
                                }"
                            >
                                <story-image
                                    fetchPriority="high"
                                    [alt]="openGalleryData.index.alt"
                                    [loading]="'eager'"
                                    class="relative z-10 block h-full w-full"
                                    [objectFit]="'object-contain'"
                                    [objectPosition]="'center'"
                                    (loaded)="loaded()"
                                >
                                    <!-- 4K / very large screens -->
                                    <story-source
                                        [src]="openGalleryData.index.url"
                                        [width]="openGalleryData.index.isPortrait ? 600 : 1350"
                                        [height]="openGalleryData.index.isPortrait ? 970 : 860"
                                        media="(min-width: 1200px)"
                                    />

                                    <!-- Desktop -->
                                    <story-source
                                        [src]="openGalleryData.index.url"
                                        [width]="openGalleryData.index.isPortrait ? 480 : 1080"
                                        [height]="openGalleryData.index.isPortrait ? 780 : 690"
                                        media="(min-width: 1000px)"
                                    />

                                    <!-- Tablet -->
                                    <story-source
                                        [src]="openGalleryData.index.url"
                                        [width]="openGalleryData.index.isPortrait ? 320 : 720"
                                        [height]="openGalleryData.index.isPortrait ? 520 : 460"
                                        media="(min-width: 500px)"
                                    />

                                    <story-source
                                        [src]="openGalleryData.index.url"
                                        [width]="openGalleryData.index.isPortrait ? 210 : 474"
                                        [height]="316"
                                    />
                                </story-image>

                                <story-image
                                    fetchPriority="high"
                                    [loading]="'eager'"
                                    [alt]="openGalleryData.prev.alt"
                                    class="absolute bottom-0 left-0 right-0 top-0 h-[0px]"
                                >
                                    <!-- 4K / very large screens -->
                                    <story-source
                                        [src]="openGalleryData.prev.url"
                                        [width]="openGalleryData.prev.isPortrait ? 600 : 1350"
                                        [height]="openGalleryData.prev.isPortrait ? 970 : 860"
                                        media="(min-width: 1500px)"
                                    />

                                    <!-- Desktop -->
                                    <story-source
                                        [src]="openGalleryData.prev.url"
                                        [width]="openGalleryData.prev.isPortrait ? 480 : 1080"
                                        [height]="openGalleryData.prev.isPortrait ? 780 : 690"
                                        media="(min-width: 1000px)"
                                    />

                                    <!-- Tablet -->
                                    <story-source
                                        [src]="openGalleryData.prev.url"
                                        [width]="openGalleryData.prev.isPortrait ? 320 : 720"
                                        [height]="openGalleryData.prev.isPortrait ? 520 : 460"
                                        media="(min-width: 500px)"
                                    />

                                    <story-source
                                        [src]="openGalleryData.prev.url"
                                        [width]="openGalleryData.prev.isPortrait ? 210 : 474"
                                        [height]="316"
                                    />
                                </story-image>

                                <story-image
                                    [alt]="openGalleryData.next.alt"
                                    fetchPriority="high"
                                    [loading]="'eager'"
                                    class="absolute bottom-0 left-0 right-0 top-0 h-[0px]"
                                >
                                    <!-- 4K / very large screens -->
                                    <story-source
                                        [src]="openGalleryData.next.url"
                                        [width]="openGalleryData.next.isPortrait ? 600 : 1350"
                                        [height]="openGalleryData.next.isPortrait ? 970 : 860"
                                        media="(min-width: 1500px)"
                                    />

                                    <!-- Desktop -->
                                    <story-source
                                        [src]="openGalleryData.next.url"
                                        [width]="openGalleryData.next.isPortrait ? 480 : 1080"
                                        [height]="openGalleryData.next.isPortrait ? 780 : 690"
                                        media="(min-width: 1000px)"
                                    />

                                    <!-- Tablet -->
                                    <story-source
                                        [src]="openGalleryData.next.url"
                                        [width]="openGalleryData.next.isPortrait ? 320 : 720"
                                        [height]="openGalleryData.next.isPortrait ? 520 : 460"
                                        media="(min-width: 500px)"
                                    />

                                    <story-source
                                        [src]="openGalleryData.next.url"
                                        [width]="openGalleryData.next.isPortrait ? 210 : 474"
                                        [height]="316"
                                    />
                                </story-image>
                            </div>
                            <button
                                class="absolute right-0 top-0 z-50 h-[52px] cursor-pointer rounded-bl bg-transparent p-2 transition-colors hover:bg-info/90"
                                (click)="closeImage()"
                                aria-label="Zavřít galerii"
                            >
                                <aui-icon icon="times" class="text-[24px]" />
                            </button>

                            <button
                                class="absolute bottom-0 left-0 top-0 z-50 m-auto h-[52px] cursor-pointer rounded-r bg-transparent p-2 transition-colors hover:bg-info/90"
                                (click)="previousImage()"
                                aria-label="Předchozí obrázek"
                            >
                                <aui-icon icon="chevron-left" class="text-[24px]" aria-label="Předchozí obrázek" />
                            </button>

                            <button
                                class="absolute bottom-0 right-0 top-0 z-50 m-auto h-[52px] cursor-pointer rounded-l bg-transparent p-2 transition-colors hover:bg-info/90"
                                (click)="nextImage()"
                                aria-label="Další obrázek"
                            >
                                <aui-icon icon="chevron-right" class="text-[24px]" aria-label="Další obrázek" />
                            </button>
                        </div>
                    </div>
                }
            } @placeholder {
                <div class="flex h-full w-full flex-col items-center justify-center">
                    <aui-loading />
                    <p class="story-gray-text mt-4">Načítání...</p>
                </div>
            }
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class AppSimplyCarouselComponent {
    private platform = inject(Platform);
    private cdRef = inject(ChangeDetectorRef);

    // migrate to angulat 20 syntax
    wrapper = viewChild<ElementRef>('wrapper');
    proxy = viewChild<ElementRef>('proxy');
    boxes = viewChild<ElementRef>('boxes');
    windowsDimensions = injectWindowsDimensions();

    openImageIndex = signal<number | null>(null);
    openGalleryDataSet = computed(() => {
        const counter = this.openImageIndex();
        const items = this.items();
        const length = items.length;

        if (counter === null || length === 0) {
            return null;
        }

        const prev = (length + counter - 1) % length;
        const index = counter % length;
        const next = (counter + 1) % length;

        if (index === null) {
            return null;
        }

        return {
            prev: { url: items[prev].url, isPortrait: items[prev].isPortrait, alt: items[prev].alt },
            index: { url: items[index].url, isPortrait: items[index].isPortrait, alt: items[index].alt },
            next: { url: items[next].url, isPortrait: items[next].isPortrait, alt: items[next].alt },
        };
    });

    gutter = input<number>(30);

    // viewWidth = signal<number>(730);

    initialized = false;
    setIndex = input<number>(0);
    slides = input<{ url: string; isPortrait: boolean; alt: string }[]>([]);

    items = signal<SliderItem[]>([]);
    loading = signal<boolean>(true);

    numBoxes = 0;
    boxHeight = computed(() => (this.windowsDimensions.width() >= 768 ? 316 : 160));

    animation: gsap.core.Timeline | undefined;
    draggable: Draggable | undefined;

    setSlider = effect(() => {
        if (!this.platform.isBrowser) {
            return;
        }

        if (!this.initialized) {
            gsap.registerPlugin(ScrollTrigger, Draggable, InertiaPlugin);
        }
        this.initialized = true;

        const slides = this.slides();
        const gutter = this.gutter();

        const boxHeight = this.boxHeight();

        this.numBoxes = slides.length;
        // this.viewport = value;
        // const wrapperWithTemp = parseFloat(gsap.getProperty(this.wrapper()?.nativeElement, 'width') + '') || 0;

        const items = this.calculateItems(slides, boxHeight, gutter);

        // const viewWidth = wrapperWithTemp;
        // const wrapWidth = items[items.length - 1].cumulativeWith + items[items.length - 1].width;

        this.items.set(items);

        this.animationEffext();
    });

    animationEffext() {
        setTimeout(() => {
            const boxes = this.boxes();
            const wrapper = this.wrapper();

            const items = this.items();
            const gutter = this.gutter();
            const boxHeight = this.boxHeight();

            if (!this.platform.isBrowser || !boxes || !items || items.length === 0 || !wrapper) {
                return;
            }

            if (boxes?.nativeElement.children.length === 0) {
                return;
            }

            const wrapWidth = items[items.length - 1].cumulativeWith + items[items.length - 1].width;

            // const wrapperWithTemp = parseFloat(gsap.getProperty(this.wrapper()?.nativeElement, 'width') + '') || 0;

            gsap.set([wrapper?.nativeElement], {
                height: this.boxHeight,
                //width: this.boxWidth * this.boxPerScreen,
                left: '50%',
                x: '-50%',
            });

            gsap.set([this.boxes()?.nativeElement], {
                height: this.boxHeight,
                width: wrapWidth,
                left: '0',
                x: -items[items.length / 3].cumulativeWith - gutter / 2,

                // left: wrapperWithTemp < 500 ? '50%' : '0',
                // x:
                //   wrapperWithTemp < 500
                //     ? '-50%'
                //     : -this.items[0].width - this.gutter / 2,
            });

            for (let i = 0; i < items.length; i++) {
                gsap.set(this.boxes()?.nativeElement.children[i], {
                    x: items[i].cumulativeWith,
                    width: items[i].width,
                    height: boxHeight,
                });
            }
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            const that = this;

            this.animation = gsap
                .timeline({
                    repeat: -1,
                    paused: true,
                })
                .add(
                    gsap.to(this.boxes()?.nativeElement.children, {
                        x: '+=' + wrapWidth,
                        ease: 'none',

                        modifiers: {
                            x: (x, target) => {
                                x = parseFloat(x + '') % wrapWidth;
                                return x + 'px';
                            },
                        },
                    }),
                    0
                );

            this.draggable = new Draggable(this.proxy()?.nativeElement, {
                type: 'x',
                trigger: this.wrapper()?.nativeElement,
                throwProps: true,
                inertia: true,

                onDrag: function (e) {
                    let value = (this!['x'] as number) / wrapWidth;
                    value = value - parseInt(value + '');
                    if (value < 0) value = 1 + value;

                    if (that.animation) that.animation.progress(value);
                },
                onThrowUpdate: function (e) {
                    let value = (this!['x'] as number) / wrapWidth;
                    value = value - parseInt(value + '');
                    if (value < 0) value = 1 + value;

                    if (that.animation) that.animation.progress(value);
                },
                snap: {
                    x: function (tempX: number) {
                        const x = tempX * -1;
                        const last = items[items.length - 1];
                        const counts = items.map((p) => p.cumulativeWith);
                        counts.push(last.cumulativeWith + last.width);

                        let a = parseInt(x / wrapWidth + '') * wrapWidth;

                        let goal = x % wrapWidth;

                        if (goal < 0) {
                            goal += wrapWidth;
                            a -= wrapWidth;
                        }

                        const closest = counts.reduce(function (prev, curr) {
                            return Math.abs(curr - goal) < Math.abs(prev - goal) ? curr : prev;
                        });

                        return (a + closest) * -1;
                    },
                },
            });

            const updateAnimation = (animation: gsap.core.Timeline | undefined, x: number, wrapWidth: number) => {
                let value = x / wrapWidth;
                value = value - parseInt(value + '');
                if (value < 0) value = 1 + value;

                if (animation) animation.progress(value);
            };

            gsap.to(this.proxy()?.nativeElement, {
                x: '0px',
                onUpdate: () => {
                    const xs = parseFloat(gsap.getProperty(this.proxy()?.nativeElement, 'x') + '');
                    updateAnimation(this.animation, xs, wrapWidth);
                },
            });
        }, 500);
    }

    calculateItems(
        items: { url: string; isPortrait: boolean; alt: string }[],
        boxHeight: number,
        gutter: number
    ): SliderItem[] {
        const value = [...items, ...items, ...items];
        let temp = 0;
        return value.map((p, index) => {
            const width = p.isPortrait ? boxHeight * (1825 / 2738) + gutter : boxHeight * (2738 / 1825) + gutter;
            const cumulativeWith = temp;
            temp += width;
            return {
                ...p,
                cumulativeWith,
                width,
                alt: p.alt,
            };
        });
    }

    openImage(index: number) {
        this.openImageIndex.set(index);
        if (this.platform.isBrowser) {
            document.body.style.overflow = 'hidden';
        }
    }

    closeImage() {
        this.openImageIndex.set(null);
        if (this.platform.isBrowser) {
            document.body.style.overflow = 'auto';
        }
    }

    previousImage() {
        this.loading.set(true);
        let index = this.openImageIndex();
        if (index === null) {
            return;
        }
        if (index === 0) {
            index = this.slides().length - 1;
        } else {
            index--;
        }

        setTimeout(() => {
            this.openImageIndex.set(index);
        }, 200);
    }

    nextImage() {
        this.loading.set(true);
        let index = this.openImageIndex();
        if (index === null) {
            return;
        }
        if (index === this.slides().length - 1) {
            index = 0;
        } else {
            index++;
        }
        setTimeout(() => {
            this.openImageIndex.set(index);
        }, 200);
    }

    loaded() {
        this.loading.set(false);
    }

    @HostListener('document:keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent) {
        if (this.openImageIndex() === null) {
            return;
        }
        switch (event.key) {
            case 'Escape':
                this.closeImage();
                break;
            case 'ArrowLeft':
                this.previousImage();
                break;
            case 'ArrowRight':
                this.nextImage();
                break;
        }
    }
}
